import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import RelativeTime from '../components/shared/RelativeTime.jsx';
import { TableSkeleton } from '../components/shared/Skeleton.jsx';
import { sortOrgsByHealth } from '../lib/platformHealthSort.js';

/**
 * Cross-org agent health — vista#792 item 3, replacing a "Coming Soon" stub.
 *
 * This is the one page that can answer "are all orgs' agents up?". The org
 * dashboard is scoped to a single org by construction, and the admin org list
 * carries an agent COUNT, which says nothing about whether those agents are
 * alive. Ties to #742 (fabric-down alerting) and #744 (host health).
 *
 * Ordering is deliberate: dark orgs first. An org with agents and none
 * connected is a customer with no monitoring, and it should not be somewhere
 * down an alphabetical list.
 */

const HEALTH_LABEL = {
  healthy: 'All connected',
  degraded: 'Partially connected',
  down: 'No agents connected',
};

const AGENT_HEALTH = {
  connected: { dot: 'bg-green-500', text: 'text-green-700', label: 'Connected' },
  stale: { dot: 'bg-red-500', text: 'text-red-700', label: 'No heartbeat' },
  // Registered and never heard from: a different action (finish the install)
  // from an agent that went quiet, so a different colour from `stale`.
  never_seen: { dot: 'bg-gray-400', text: 'text-gray-500', label: 'Never connected' },
};

function agentHealth(h) {
  return AGENT_HEALTH[h] || { dot: 'bg-yellow-500', text: 'text-yellow-700', label: h };
}

function Totals({ totals, staleAfter }) {
  const cards = [
    { label: 'Organizations', value: totals.orgs },
    { label: 'Agents', value: totals.agents },
    { label: 'Connected', value: totals.connected, tone: 'text-green-700' },
    {
      label: 'No heartbeat',
      value: totals.stale,
      tone: totals.stale > 0 ? 'text-red-700' : undefined,
      sub: `over ${Math.round(staleAfter / 60)} min`,
    },
    {
      label: 'Orgs with nothing connected',
      value: totals.orgs_dark,
      tone: totals.orgs_dark > 0 ? 'text-red-700' : undefined,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      {cards.map((c) => (
        <div key={c.label} className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{c.label}</div>
          <div className={`mt-1 text-2xl font-semibold ${c.tone || 'text-gray-900'}`}>{c.value}</div>
          {c.sub && <div className="mt-0.5 text-xs text-gray-400">{c.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export default function PlatformHealth() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-health'],
    queryFn: () => api.get('/vista-admin/platform-health').then((r) => r.data),
    // Agents beat every 30s; polling at 30s keeps the page roughly as fresh as
    // the data can be without asking for what has not changed.
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Platform Health</h1>
        <TableSkeleton rows={5} cols={4} message="Loading platform health…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Platform Health</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Could not load platform health: {error.message}
        </div>
      </div>
    );
  }

  // Dark orgs first, then partial, then healthy — alphabetical within each band
  // so the list is stable between 30s polls. Pure and unit-tested, since this
  // console has no DOM harness and the ordering is the part with a decision.
  const orgs = sortOrgsByHealth(data?.orgs);

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Platform Health</h1>
        <span className="text-xs text-gray-400">
          as of <RelativeTime ts={data?.generated_at} />
        </span>
      </div>

      <Totals totals={data.totals} staleAfter={data.stale_after_seconds} />

      {orgs.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <h2 className="text-lg font-semibold text-gray-900">No agents registered</h2>
          <p className="mt-2 text-sm text-gray-500">
            Agent health appears here once an organization registers its first agent.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {orgs.map((org) => (
            <div key={org.org_id ?? 'unassigned'} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${
                      org.health === 'healthy'
                        ? 'bg-green-500'
                        : org.health === 'degraded'
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }`}
                  />
                  {org.org_id ? (
                    <Link
                      to={`/organizations/${org.org_id}`}
                      className="text-sm font-semibold text-blue-600 hover:underline"
                    >
                      {org.org_name}
                    </Link>
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">{org.org_name}</span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {org.connected_count} of {org.agent_count} connected — {HEALTH_LABEL[org.health] || org.health}
                </span>
              </div>
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Agent</th>
                    <th className="px-4 py-2 font-medium">State</th>
                    <th className="px-4 py-2 font-medium">Last heartbeat</th>
                    <th className="px-4 py-2 font-medium">Reported status</th>
                  </tr>
                </thead>
                <tbody>
                  {org.agents.map((a) => {
                    const h = agentHealth(a.health);
                    return (
                      <tr key={a.agent_id} className="border-t border-gray-100">
                        <td className="px-4 py-2 font-medium text-gray-900">{a.name}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center gap-1.5 ${h.text}`}>
                            <span className={`inline-block h-1.5 w-1.5 rounded-full ${h.dot}`} />
                            {h.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          <RelativeTime ts={a.last_seen} fallback="Never" />
                        </td>
                        {/* Both are shown on purpose: the agent's own claim and
                            what the heartbeat proves. When they disagree — a row
                            saying `active` with no heartbeat for an hour — the
                            disagreement IS the finding. */}
                        <td className="px-4 py-2 text-gray-400">{a.status || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
